import { ComplexTable } from '@synergy/table';
import { Col, notification, Row } from 'antd';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import query from '../../../../server/constants.api';
import useFetch from "../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../styles/global/Pages.styled";
import { AUTH_USER_POOL_ID } from '../../../../utils/config/configs';
import { tags, tenants, types, zones } from '../../../../utils/config/widgetsConfig';
import Spinner from '../../Atomics/Spinner';
import { Text } from '../../Atomics/Text';
import permissionToEdit from './AccessUtils';
import { getCustomizedButtonsForConfiguration } from './ConfigurationUtils';
import { EditPopupConfigurations } from './EditPopupConfigurations';

export const UsersConfigurations = ({ ...props }) => {
    // Obtém a função de tradução do hook useTranslation para uso na tradução de textos
    const allowedToWrite = permissionToEdit(['Configurations', 'UsersConfigurations'])
    const { t } = useTranslation();
    const tenant = JSON.parse(localStorage.getItem('unified_nova_tenant'));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isTofecth, setIsToFetch] = useState(false);
    const [bodyToPost, setBodyToPost] = useState(null);
    const [isToEdit, setIsToEdit] = useState(false);

    const {
        data: userList,
        refetch: refetchUserList
    } = useFetch("/users", "userList", { tenant: tenant[0], userPool: AUTH_USER_POOL_ID }, null, null, query.middlewareURL);

    //API para adicionar ou editar um User
    const {
        data: addOrEditUser,
        isFetching: addOrEditUserIsFetching,
        error: addOrEditUserError,
        isSuccess: addOrEditUserIsSuccess,
        isError: addOrEditUserIsError
    } = useFetch("/users", 'editUser',
        bodyToPost, isTofecth, isToEdit ? 'PUT' : 'POST', query.middlewareURL);

    useEffect(() => {
        if (addOrEditUserIsError) {
            const errorMessage = addOrEditUserError?.response?.data?.detailedMessage || t('An error occurred while saving the user.');

            // Usar uma notificação para exibir o erro
            notification.error({
                message: t('Error'),
                description: errorMessage,
                duration: 3,  // Define a duração da notificação (3 segundos)
            });
        } else if (addOrEditUser && !addOrEditUserIsFetching && addOrEditUserIsSuccess) {
            refetchUserList();
            setSelectedRecord(null);
        }
        setIsToFetch(false);
    }, [addOrEditUserIsFetching, addOrEditUserIsError]);

    function handleOkModal(data) {
        //No PUT são obrigatórios com valor "username", "tenant", "phone_number", "email", "temporary_password", "mfa_active"
        //No POST é obrigatório o "username"
        const copyData = {
            ...data,
            mfa_active: false,
            temporary_password: data.temporary_password ? data.temporary_password : 'Teste123?',
            tenant: JSON.stringify(data.tenant),
            zones: data.zones ? JSON.stringify(data.zones) : JSON.stringify([]),
            tags: data.tags ? JSON.stringify(data.tags) : JSON.stringify([])
        };
        setBodyToPost({ ...selectedRecord, ...copyData });
        setIsToFetch(true);
        setIsModalOpen(false);
        setSelectedRecord(null);
    }

    function handleEdit(record) {
        setIsToEdit(true); // Ativa a flag de edição
        setSelectedRecord(record);  // Atualiza o estado com o registro selecionado
        setIsModalOpen(true); // Abre a modal
    }

    function handleCloseModal() {
        setIsModalOpen(false);  // Fecha a modal
        setSelectedRecord(null); // Limpa o registro selecionado
    }


    const tableConfiguration = {
        collumnsConfiguration: [
            {
                title: t('Name'),
                dataIndex: 'username',
                editable: true,
                editType: 'input'
            },
            {
                title: t('Phone Number'),
                dataIndex: 'phone_number',
                editType: 'input'
            },
            {
                title: t('Email'),
                dataIndex: 'email',
                editType: 'input'
            },
            {
                title: t('Tenant'),
                dataIndex: 'tenant',
                tags: true,
                fieldTypeId: 4
            },
            {
                title: t('Zones'),
                dataIndex: 'zones',
                tags: true,
                fieldTypeId: 4
            },
            {
                title: t('Tags'),
                dataIndex: 'tags',
                tags: true,
                fieldTypeId: 4
            }
        ]
    };

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Users List')}</Text>
                                </PageTitle>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        {userList && !addOrEditUserIsFetching ?
                            <ComplexTable
                                fetchData={userList.map((x, index) => { return { ...x, id: index } })}
                                language={i18next.resolvedLanguage}
                                tableConfiguration={tableConfiguration}
                                editButton={allowedToWrite}
                                fixedActions={true}
                                handleEdit={(record) => { handleEdit({ ...emptyRecord, ...record }) }}
                                customizedButtons={
                                    getCustomizedButtonsForConfiguration(allowedToWrite,
                                        () => { setIsToEdit(false); setSelectedRecord(emptyRecord); setIsModalOpen(true) }, t)
                                }
                            />
                            :
                            <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Spinner stroke="green" />
                            </div>
                        }
                    </Col>
                    <EditPopupConfigurations
                        openModal={isModalOpen}
                        closeModal={handleCloseModal}
                        isToEdit={isToEdit}
                        onOkModal={(data) => handleOkModal(data)}
                        selectedRecord={selectedRecord ? serealizeRecordForModal(selectedRecord, tableConfiguration.collumnsConfiguration) : null} />
                </Row>
            </PageSection>
        </PageContent>
    )
}

//Serializer para tornar a modal no formato correto para editar um User, como uma configuração para a Modal
const serealizeRecordForModal = (selectedRecord, tableConfiguration) => {
    // const keysToKeep = ['username', 'email', 'phone_number', 'tenant', 'zones', 'tags'];
    const keysToKeep = tableConfiguration.map(x => x.dataIndex);
    const result = Object.entries(selectedRecord).map((x, index) => {
        const [key, value] = x;
        return {
            key: key,
            value: value,
            fieldTypeOf: tableConfiguration.find(y => y.dataIndex === key)?.fieldTypeId ? types.get(tableConfiguration.find(y => y.dataIndex === key).fieldTypeId) : types.get(2),
            required: key === 'username' || key === 'email' || key === 'phone_number' || key === 'tenant',
            options: returnOptions(key),
            size: tableConfiguration.find(y => y.dataIndex === key)?.size ?? null,
            name: tableConfiguration.find(y => y.dataIndex === key)?.title ?? null
        };
    });
    return result.filter(x => keysToKeep.includes(x.key)).sort((a, b) => keysToKeep.indexOf(a.key) - keysToKeep.indexOf(b.key));
};

const emptyRecord = {
    "username": null,
    "email": null,
    "tenant": null,
    "zones": null,
    "tags": null,
    "phone_number": null,
    "temporary_password": null,
    "mfa_active": false
}

export function returnOptions(key) {
    switch (key) {
        case 'zones':
            return zones.map(x => { return { label: x, value: x } });
        case 'tags':
            return tags.map(x => { return { label: x, value: x } });
        case 'tenant':
            return tenants.map(x => { return { label: x, value: x } });
        default:
            return null;
    }
}